import { useState } from 'react';

/**
 * Data Freshness Indicator (UX-002)
 *
 * Shows data provenance information:
 * - Source (Nielsen, client data, synthetic, industry benchmark)
 * - Fidelity score (1-10)
 * - Last updated date
 *
 * ANA QUOTE: "If it is not possible to update, it would be interesting to say
 * when this date is. If it is information that they pass you in December last year,
 * it is information from two years ago, so that over time, when the person looks,
 * they do not say 'Oh, no, that is not true,' but 'the last information we had.'"
 */

// =============================================================================
// TYPES
// =============================================================================

export type DataSource = 'nielsen' | 'client' | 'synthetic' | 'industry' | 'mixed';

export interface DataProvenance {
  /** Primary data source */
  source: DataSource;
  /** Fidelity score 1-10 (10 = highest quality) */
  fidelity: number;
  /** Last updated date */
  lastUpdated: Date | string;
  /** Optional notes about the data */
  notes?: string;
  /** Whether data is considered stale (> 6 months old) */
  isStale?: boolean;
}

interface DataFreshnessIndicatorProps {
  provenance: DataProvenance;
  /** Compact mode shows just an icon, expanded mode shows full details */
  variant?: 'compact' | 'badge' | 'full';
  /** Position of popover */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Language */
  locale?: 'en' | 'es';
}

// =============================================================================
// SOURCE CONFIG
// =============================================================================

const sourceConfig: Record<DataSource, {
  label: { en: string; es: string };
  shortLabel: { en: string; es: string };
  icon: string;
  color: string;
  description: { en: string; es: string };
}> = {
  nielsen: {
    label: { en: 'Nielsen Data', es: 'Datos Nielsen' },
    shortLabel: { en: 'Nielsen', es: 'Nielsen' },
    icon: '📊',
    color: 'text-blue-400',
    description: {
      en: 'Market research data from Nielsen retail audits',
      es: 'Datos de investigación de mercado de auditorías minoristas de Nielsen',
    },
  },
  client: {
    label: { en: 'Client Data', es: 'Datos del Cliente' },
    shortLabel: { en: 'Client', es: 'Cliente' },
    icon: '🏢',
    color: 'text-success-400',
    description: {
      en: 'Data provided directly by the client (POS, sales, etc.)',
      es: 'Datos proporcionados directamente por el cliente (POS, ventas, etc.)',
    },
  },
  synthetic: {
    label: { en: 'Synthetic/Modeled', es: 'Sintético/Modelado' },
    shortLabel: { en: 'Synthetic', es: 'Sintético' },
    icon: '🤖',
    color: 'text-warning-400',
    description: {
      en: 'AI-generated or model-estimated data based on available inputs',
      es: 'Datos generados por IA o estimados por modelo basados en entradas disponibles',
    },
  },
  industry: {
    label: { en: 'Industry Benchmark', es: 'Referencia Industria' },
    shortLabel: { en: 'Benchmark', es: 'Referencia' },
    icon: '📈',
    color: 'text-text-secondary',
    description: {
      en: 'Industry averages or benchmarks (not client-specific)',
      es: 'Promedios de la industria o referencias (no específicos del cliente)',
    },
  },
  mixed: {
    label: { en: 'Mixed Sources', es: 'Fuentes Mixtas' },
    shortLabel: { en: 'Mixed', es: 'Mixto' },
    icon: '🔀',
    color: 'text-primary-400',
    description: {
      en: 'Combination of multiple data sources',
      es: 'Combinación de múltiples fuentes de datos',
    },
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function formatDate(date: Date | string, locale: 'en' | 'es'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString(locale === 'es' ? 'es-CO' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getRelativeTime(date: Date | string, locale: 'en' | 'es'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return locale === 'es' ? 'Hoy' : 'Today';
  if (diffDays === 1) return locale === 'es' ? 'Ayer' : 'Yesterday';
  if (diffDays < 7) return locale === 'es' ? `Hace ${diffDays} días` : `${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return locale === 'es' ? `Hace ${weeks} semana${weeks > 1 ? 's' : ''}` : `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return locale === 'es' ? `Hace ${months} mes${months > 1 ? 'es' : ''}` : `${months} month${months > 1 ? 's' : ''} ago`;
  }
  const years = Math.floor(diffDays / 365);
  return locale === 'es' ? `Hace ${years} año${years > 1 ? 's' : ''}` : `${years} year${years > 1 ? 's' : ''} ago`;
}

function isDataStale(date: Date | string): boolean {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30);
  return diffMonths > 6;
}

function getFidelityColor(score: number): string {
  if (score >= 8) return 'text-success-400';
  if (score >= 5) return 'text-warning-400';
  return 'text-danger-400';
}

function getFidelityLabel(score: number, locale: 'en' | 'es'): string {
  if (score >= 8) return locale === 'es' ? 'Alta' : 'High';
  if (score >= 5) return locale === 'es' ? 'Media' : 'Medium';
  return locale === 'es' ? 'Baja' : 'Low';
}

// =============================================================================
// COMPACT INDICATOR (icon only with hover)
// =============================================================================

function CompactIndicator({
  provenance,
  locale = 'en',
}: {
  provenance: DataProvenance;
  locale?: 'en' | 'es';
}) {
  const [isHovered, setIsHovered] = useState(false);
  const config = sourceConfig[provenance.source];
  const stale = provenance.isStale ?? isDataStale(provenance.lastUpdated);

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        className={`flex items-center gap-1 text-xs ${config.color} hover:opacity-80 transition-opacity cursor-help`}
        aria-label={`Data source: ${config.label[locale]}`}
      >
        <span>{config.icon}</span>
        {stale && <span className="text-danger-400 text-[10px]">!</span>}
      </button>

      {/* Hover popover */}
      {isHovered && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 bg-surface-800 border border-surface-600 rounded-lg shadow-xl">
          <div className="space-y-2 text-xs">
            {/* Source */}
            <div className="flex items-center justify-between">
              <span className="text-text-tertiary">{locale === 'es' ? 'Fuente' : 'Source'}:</span>
              <span className={config.color}>{config.shortLabel[locale]}</span>
            </div>
            {/* Fidelity */}
            <div className="flex items-center justify-between">
              <span className="text-text-tertiary">{locale === 'es' ? 'Fidelidad' : 'Fidelity'}:</span>
              <span className={getFidelityColor(provenance.fidelity)}>
                {provenance.fidelity}/10 ({getFidelityLabel(provenance.fidelity, locale)})
              </span>
            </div>
            {/* Last Updated */}
            <div className="flex items-center justify-between">
              <span className="text-text-tertiary">{locale === 'es' ? 'Actualizado' : 'Updated'}:</span>
              <span className={stale ? 'text-danger-400' : 'text-text-secondary'}>
                {getRelativeTime(provenance.lastUpdated, locale)}
              </span>
            </div>
            {/* Stale warning */}
            {stale && (
              <div className="pt-2 border-t border-surface-600 text-danger-400">
                {locale === 'es'
                  ? '⚠️ Datos de más de 6 meses'
                  : '⚠️ Data is over 6 months old'}
              </div>
            )}
          </div>
          {/* Arrow */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-surface-800" />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// BADGE INDICATOR (inline with text)
// =============================================================================

function BadgeIndicator({
  provenance,
  locale = 'en',
}: {
  provenance: DataProvenance;
  locale?: 'en' | 'es';
}) {
  const [isHovered, setIsHovered] = useState(false);
  const config = sourceConfig[provenance.source];
  const stale = provenance.isStale ?? isDataStale(provenance.lastUpdated);

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs cursor-help ${
          stale ? 'bg-danger-500/10 border border-danger-500/30' : 'bg-surface-600/50 border border-surface-500/30'
        }`}
      >
        <span>{config.icon}</span>
        <span className={stale ? 'text-danger-400' : 'text-text-secondary'}>
          {config.shortLabel[locale]}
        </span>
        <span className="text-surface-400">•</span>
        <span className={getFidelityColor(provenance.fidelity)}>
          {provenance.fidelity}/10
        </span>
      </span>

      {/* Hover popover */}
      {isHovered && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-surface-800 border border-surface-600 rounded-lg shadow-xl">
          <div className="space-y-2 text-xs">
            <div className="font-medium text-white mb-2">{config.label[locale]}</div>
            <p className="text-text-secondary">{config.description[locale]}</p>
            <div className="pt-2 border-t border-surface-600 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-text-tertiary">{locale === 'es' ? 'Fidelidad' : 'Fidelity'}:</span>
                <span className={getFidelityColor(provenance.fidelity)}>
                  {provenance.fidelity}/10
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-tertiary">{locale === 'es' ? 'Actualizado' : 'Updated'}:</span>
                <span className={stale ? 'text-danger-400' : 'text-text-secondary'}>
                  {formatDate(provenance.lastUpdated, locale)}
                </span>
              </div>
            </div>
            {provenance.notes && (
              <div className="pt-2 border-t border-surface-600 text-text-secondary italic">
                {provenance.notes}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// FULL INDICATOR (expanded view)
// =============================================================================

function FullIndicator({
  provenance,
  locale = 'en',
}: {
  provenance: DataProvenance;
  locale?: 'en' | 'es';
}) {
  const config = sourceConfig[provenance.source];
  const stale = provenance.isStale ?? isDataStale(provenance.lastUpdated);

  return (
    <div
      className={`flex items-center gap-4 px-3 py-2 rounded-lg text-xs ${
        stale ? 'bg-danger-500/5 border border-danger-500/20' : 'bg-surface-700/30 border border-surface-600/30'
      }`}
    >
      {/* Source */}
      <div className="flex items-center gap-1.5">
        <span>{config.icon}</span>
        <span className={config.color}>{config.shortLabel[locale]}</span>
      </div>

      <span className="text-surface-500">|</span>

      {/* Fidelity */}
      <div className="flex items-center gap-1.5">
        <span className="text-text-tertiary">{locale === 'es' ? 'Fidelidad' : 'Fidelity'}:</span>
        <span className={getFidelityColor(provenance.fidelity)}>
          {provenance.fidelity}/10
        </span>
      </div>

      <span className="text-surface-500">|</span>

      {/* Last Updated */}
      <div className="flex items-center gap-1.5">
        <span className="text-text-tertiary">{locale === 'es' ? 'Actualizado' : 'Updated'}:</span>
        <span className={stale ? 'text-danger-400' : 'text-text-secondary'}>
          {getRelativeTime(provenance.lastUpdated, locale)}
        </span>
        {stale && (
          <span className="text-danger-400" title={locale === 'es' ? 'Datos antiguos' : 'Stale data'}>
            ⚠️
          </span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// MAIN EXPORT
// =============================================================================

export function DataFreshnessIndicator({
  provenance,
  variant = 'compact',
  locale = 'en',
}: DataFreshnessIndicatorProps) {
  switch (variant) {
    case 'compact':
      return <CompactIndicator provenance={provenance} locale={locale} />;
    case 'badge':
      return <BadgeIndicator provenance={provenance} locale={locale} />;
    case 'full':
      return <FullIndicator provenance={provenance} locale={locale} />;
    default:
      return <CompactIndicator provenance={provenance} locale={locale} />;
  }
}

// =============================================================================
// SECTION HEADER WITH FRESHNESS
// =============================================================================

interface SectionHeaderProps {
  title: string;
  provenance?: DataProvenance;
  locale?: 'en' | 'es';
  children?: React.ReactNode;
}

export function SectionHeaderWithFreshness({
  title,
  provenance,
  locale = 'en',
  children,
}: SectionHeaderProps) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        {provenance && (
          <DataFreshnessIndicator provenance={provenance} variant="badge" locale={locale} />
        )}
      </div>
      {children}
    </div>
  );
}

// =============================================================================
// PRESET PROVENANCES (for common scenarios)
// =============================================================================

export const PROVENANCE_PRESETS = {
  nielsenRecent: (lastUpdated: Date | string = new Date()): DataProvenance => ({
    source: 'nielsen',
    fidelity: 9,
    lastUpdated,
    notes: 'Nielsen retail audit data',
  }),
  clientData: (lastUpdated: Date | string = new Date()): DataProvenance => ({
    source: 'client',
    fidelity: 10,
    lastUpdated,
    notes: 'Client-provided transaction data',
  }),
  syntheticModel: (lastUpdated: Date | string = new Date()): DataProvenance => ({
    source: 'synthetic',
    fidelity: 6,
    lastUpdated,
    notes: 'AI-generated based on market patterns',
  }),
  industryBenchmark: (): DataProvenance => ({
    source: 'industry',
    fidelity: 4,
    lastUpdated: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), // 90 days ago
    notes: 'Industry average benchmarks',
  }),
};

export default DataFreshnessIndicator;
